/**
 * RampViewConfigPanel - Collapsible panel for configuring aircraft label & warning visibility
 *
 * Floating panel (top-right of map) with collapsible groups of checkboxes
 * that control which data fields appear as sticky labels on all aircraft.
 * Warnings use a 3-state toggle (Full | Icons | Off) with collapsible groups
 * containing individual per-warning checkboxes and a group-level toggle.
 */

import {
  Tags, ChevronDown, ChevronRight, RotateCcw, Box, Shapes, Map, AlertTriangle,
  Eye, CircleDot, EyeOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import {
  useRampViewConfigStore,
  RAMP_LABEL_FIELDS,
  RAMP_LABEL_GROUPS,
  STATUS_GROUP_CONFIG,
  STATUS_ROW_ITEMS,
  type RampLabelFieldConfig,
  type WarningDisplayMode,
  type StatusRowItemConfig,
} from '@/stores/rampViewConfigStore'
import type { StatusRowGroup } from '@/lib/utils/compute-status-rows'
import { cn } from '@/lib/utils'

// ── Severity dot colors for individual warning items ──

const SEVERITY_DOT_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  warning: 'bg-orange-500',
  notice: 'bg-yellow-500',
  info: 'bg-blue-500',
}

// ── Collapsible Data Field Group ──

function CollapsibleFieldGroup({
  groupKey,
  label,
  fields,
}: {
  groupKey: string
  label: string
  fields: RampLabelFieldConfig[]
}) {
  const enabledFields = useRampViewConfigStore((state) => state.enabledFields)
  const toggleField = useRampViewConfigStore((state) => state.toggleField)
  const collapsedGroups = useRampViewConfigStore((state) => state.collapsedGroups)
  const toggleGroupCollapsed = useRampViewConfigStore((state) => state.toggleGroupCollapsed)

  const isCollapsed = collapsedGroups.includes(groupKey)
  const enabledCount = fields.filter((f) => enabledFields.includes(f.field)).length

  return (
    <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroupCollapsed(groupKey)}>
      <CollapsibleTrigger asChild>
        <button className="flex items-center justify-between w-full text-left hover:bg-muted/50 rounded px-1 py-1 -mx-1">
          <div className="flex items-center gap-1">
            {isCollapsed ? (
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            )}
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {label}
            </span>
          </div>
          {enabledCount > 0 && (
            <span className="text-[9px] text-muted-foreground tabular-nums">
              {enabledCount}/{fields.length}
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5 pl-4 pt-0.5">
          {fields.map((fieldConfig) => (
            <label
              key={fieldConfig.field}
              className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1"
            >
              <Checkbox
                checked={enabledFields.includes(fieldConfig.field)}
                onCheckedChange={() => toggleField(fieldConfig.field)}
                className="h-3.5 w-3.5"
              />
              <span className="text-xs select-none">{fieldConfig.label}</span>
            </label>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ── Warning Display Mode Selector ──

const DISPLAY_MODE_OPTIONS: { value: WarningDisplayMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'full', label: 'Full', icon: Eye },
  { value: 'compact', label: 'Icons', icon: CircleDot },
  { value: 'off', label: 'Off', icon: EyeOff },
]

// ── Collapsible Warning Group (group-level toggle + individual items) ──

function CollapsibleWarningGroup({
  groupKey,
  label,
  items,
}: {
  groupKey: StatusRowGroup
  label: string
  items: StatusRowItemConfig[]
}) {
  const enabledStatusGroups = useRampViewConfigStore((state) => state.enabledStatusGroups)
  const disabledStatusRowKeys = useRampViewConfigStore((state) => state.disabledStatusRowKeys)
  const toggleStatusRowKey = useRampViewConfigStore((state) => state.toggleStatusRowKey)
  const setStatusGroupEnabled = useRampViewConfigStore((state) => state.setStatusGroupEnabled)
  const collapsedGroups = useRampViewConfigStore((state) => state.collapsedGroups)
  const toggleGroupCollapsed = useRampViewConfigStore((state) => state.toggleGroupCollapsed)

  const isCollapsed = collapsedGroups.includes(`warning-${groupKey}`)
  const isGroupEnabled = enabledStatusGroups.includes(groupKey)
  const enabledCount = items.filter(
    (item) => isGroupEnabled && !disabledStatusRowKeys.includes(item.key)
  ).length
  const allEnabled = enabledCount === items.length
  const someEnabled = enabledCount > 0 && !allEnabled

  const handleGroupToggle = () => {
    if (allEnabled || someEnabled) {
      // Disable the whole group
      setStatusGroupEnabled(groupKey, false)
    } else {
      // Enable the whole group (clears individual disables too)
      setStatusGroupEnabled(groupKey, true)
    }
  }

  return (
    <Collapsible
      open={!isCollapsed}
      onOpenChange={() => toggleGroupCollapsed(`warning-${groupKey}`)}
    >
      <div className="flex items-center gap-1">
        {/* Group-level checkbox (stops propagation so clicking it doesn't toggle collapse) */}
        <div
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
        >
          <Checkbox
            checked={allEnabled ? true : someEnabled ? 'indeterminate' : false}
            onCheckedChange={handleGroupToggle}
            className="h-3.5 w-3.5"
          />
        </div>

        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between flex-1 text-left hover:bg-muted/50 rounded px-1 py-1 -mx-1">
            <div className="flex items-center gap-1">
              {isCollapsed ? (
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              )}
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                {label}
              </span>
            </div>
            {enabledCount > 0 && (
              <span className="text-[9px] text-muted-foreground tabular-nums">
                {enabledCount}/{items.length}
              </span>
            )}
          </button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="space-y-0.5 pl-5 pt-0.5">
          {items.map((item) => {
            const isEnabled = isGroupEnabled && !disabledStatusRowKeys.includes(item.key)
            return (
              <label
                key={item.key}
                className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1"
              >
                <Checkbox
                  checked={isEnabled}
                  onCheckedChange={() => {
                    if (!isGroupEnabled) {
                      // Enable group first, then this item is auto-enabled
                      setStatusGroupEnabled(groupKey, true)
                    } else {
                      toggleStatusRowKey(item.key)
                    }
                  }}
                  className="h-3.5 w-3.5"
                />
                <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', SEVERITY_DOT_COLORS[item.severity])} />
                <span className="text-xs select-none">{item.label}</span>
              </label>
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ── Main Panel ──

export function RampViewConfigPanel() {
  const isPanelOpen = useRampViewConfigStore((state) => state.isPanelOpen)
  const setPanelOpen = useRampViewConfigStore((state) => state.setPanelOpen)
  const enabledFields = useRampViewConfigStore((state) => state.enabledFields)
  const resetToDefaults = useRampViewConfigStore((state) => state.resetToDefaults)
  const aircraftRenderMode = useRampViewConfigStore((state) => state.aircraftRenderMode)
  const setAircraftRenderMode = useRampViewConfigStore((state) => state.setAircraftRenderMode)
  const showAreaLabels = useRampViewConfigStore((state) => state.showAreaLabels)
  const setShowAreaLabels = useRampViewConfigStore((state) => state.setShowAreaLabels)
  const showUnitLabels = useRampViewConfigStore((state) => state.showUnitLabels)
  const setShowUnitLabels = useRampViewConfigStore((state) => state.setShowUnitLabels)
  const showLeaderLines = useRampViewConfigStore((state) => state.showLeaderLines)
  const setShowLeaderLines = useRampViewConfigStore((state) => state.setShowLeaderLines)
  const showSpritePreviews = useRampViewConfigStore((state) => state.showSpritePreviews)
  const setShowSpritePreviews = useRampViewConfigStore((state) => state.setShowSpritePreviews)
  const showRoofs = useRampViewConfigStore((state) => state.showRoofs)
  const setShowRoofs = useRampViewConfigStore((state) => state.setShowRoofs)
  const warningDisplayMode = useRampViewConfigStore((state) => state.warningDisplayMode)
  const setWarningDisplayMode = useRampViewConfigStore((state) => state.setWarningDisplayMode)
  const enabledStatusGroups = useRampViewConfigStore((state) => state.enabledStatusGroups)
  const disabledStatusRowKeys = useRampViewConfigStore((state) => state.disabledStatusRowKeys)
  const collapsedGroups = useRampViewConfigStore((state) => state.collapsedGroups)

  const enabledCount = enabledFields.length

  // Count individually enabled warning items
  const activeWarningItemCount = warningDisplayMode !== 'off'
    ? STATUS_ROW_ITEMS.filter(
        (item) =>
          enabledStatusGroups.includes(item.group) &&
          !disabledStatusRowKeys.includes(item.key)
      ).length
    : 0

  // Check if any settings differ from defaults
  const hasNonDefaultSettings =
    enabledCount !== 1 ||
    !enabledFields.includes('tailNumber') ||
    aircraftRenderMode !== 'full' ||
    !showAreaLabels ||
    showUnitLabels ||
    showLeaderLines ||
    warningDisplayMode !== 'full' ||
    enabledStatusGroups.length !== 3 ||
    disabledStatusRowKeys.length !== 0 ||
    collapsedGroups.length !== 0

  return (
    <div className="absolute top-4 right-4 z-20 w-[240px]">
      <Collapsible open={isPanelOpen} onOpenChange={setPanelOpen}>
        <CollapsibleTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between bg-background/95 backdrop-blur-sm border shadow-sm h-8 text-xs"
          >
            <span className="flex items-center gap-1.5">
              <Tags className="w-3.5 h-3.5" />
              Ramp View
            </span>
            <span className="flex items-center gap-1.5">
              {(enabledCount > 0 || activeWarningItemCount > 0) && (
                <span className="text-muted-foreground text-[10px]">
                  {enabledCount + activeWarningItemCount} on
                </span>
              )}
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 transition-transform duration-200',
                  isPanelOpen && 'rotate-180'
                )}
              />
            </span>
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-1 p-3 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg space-y-3 max-h-[70vh] overflow-y-auto">
            {/* ── Map Labels ── */}
            <div className="pb-2 border-b">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                <Map className="w-3 h-3 inline mr-1" />
                Map Labels
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1">
                  <span className="text-xs select-none">Area Names</span>
                  <Switch
                    checked={showAreaLabels}
                    onCheckedChange={setShowAreaLabels}
                    className="h-4 w-7"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1">
                  <span className="text-xs select-none">Unit Codes</span>
                  <Switch
                    checked={showUnitLabels}
                    onCheckedChange={setShowUnitLabels}
                    className="h-4 w-7"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1">
                  <span className="text-xs select-none">Leader Lines</span>
                  <Switch
                    checked={showLeaderLines}
                    onCheckedChange={setShowLeaderLines}
                    className="h-4 w-7"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1">
                  <span className="text-xs select-none">Loading Sprites</span>
                  <Switch
                    checked={showSpritePreviews}
                    onCheckedChange={setShowSpritePreviews}
                    className="h-4 w-7"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 -mx-1">
                  <span className="text-xs select-none">Roofs</span>
                  <Switch
                    checked={showRoofs}
                    onCheckedChange={setShowRoofs}
                    className="h-4 w-7"
                  />
                </label>
              </div>
            </div>

            {/* ── Aircraft Rendering Mode ── */}
            <div className="pb-2 border-b">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                Rendering
              </div>
              <div className="flex gap-1">
                <Button
                  variant={aircraftRenderMode === 'full' ? 'default' : 'outline'}
                  size="sm"
                  className="h-6 text-[10px] flex-1"
                  onClick={() => setAircraftRenderMode('full')}
                >
                  <Box className="w-3 h-3 mr-1" />
                  Detailed
                </Button>
                <Button
                  variant={aircraftRenderMode === 'simplified' ? 'default' : 'outline'}
                  size="sm"
                  className="h-6 text-[10px] flex-1"
                  onClick={() => setAircraftRenderMode('simplified')}
                >
                  <Shapes className="w-3 h-3 mr-1" />
                  Simplified
                </Button>
              </div>
            </div>

            {/* ── Data Labels (collapsible groups) ── */}
            <div className="pb-2 border-b space-y-1">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                <Tags className="w-3 h-3 inline mr-1" />
                Data Labels
              </div>
              {RAMP_LABEL_GROUPS.map((group) => {
                const fields = RAMP_LABEL_FIELDS.filter((f) => f.group === group.key)
                return (
                  <CollapsibleFieldGroup
                    key={group.key}
                    groupKey={`data-${group.key}`}
                    label={group.label}
                    fields={fields}
                  />
                )
              })}
            </div>

            {/* ── Warnings (3-state mode + collapsible groups with individual items) ── */}
            <div className="pb-2 border-b space-y-2">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Warnings
              </div>

              {/* Mode selector — 3-button segmented group */}
              <div className="flex gap-1">
                {DISPLAY_MODE_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    variant={warningDisplayMode === value ? 'default' : 'outline'}
                    size="sm"
                    className="h-6 text-[10px] flex-1"
                    onClick={() => setWarningDisplayMode(value)}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {label}
                  </Button>
                ))}
              </div>

              {/* Collapsible warning groups with individual toggles — shown when mode != 'off' */}
              {warningDisplayMode !== 'off' && (
                <div className="space-y-1">
                  {STATUS_GROUP_CONFIG.map((group) => {
                    const items = STATUS_ROW_ITEMS.filter((item) => item.group === group.key)
                    return (
                      <CollapsibleWarningGroup
                        key={group.key}
                        groupKey={group.key}
                        label={group.label}
                        items={items}
                      />
                    )
                  })}
                </div>
              )}
            </div>

            {/* ── Reset to defaults ── */}
            {hasNonDefaultSettings ? (
              <div className="pt-1">
                <button
                  onClick={resetToDefaults}
                  className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset to defaults
                </button>
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
